import React from 'react';
import {useTranslation} from 'react-i18next';
import {
	DefaultButtonCompat as DefaultButton,
	DialogCompat as Dialog,
	DialogFooterCompat as DialogFooter,
	DialogType,
	MessageBarType,
	PrimaryButtonCompat as PrimaryButton,
} from 'components/FluentUICompat';
import {useNotificationBar} from 'hooks';
import {useSyncLatestGetexRegulationsMutation} from './hooks/GetexRegulations.generated';

interface Props {
	isOpen: boolean;
	onDismiss: () => void;
}

export const SyncLatestDialog: React.FC<Props> = ({isOpen, onDismiss}) => {
	const {t} = useTranslation('features/getexregulation', {
		keyPrefix: 'SyncLatestDialog',
	});

	const {setMessage} = useNotificationBar();

	const [syncLatest] = useSyncLatestGetexRegulationsMutation();

	const handleConfirm = () => {
		onDismiss();

		setMessage({
			message: t('InProgressMessage'),
			timeout: 10000,
			options: {
				messageBarType: MessageBarType.info,
			},
		});

		void (async () => {
			try {
				const result = await syncLatest({
					variables: {
						syncAttachments: true,
					},
				});
				const resultObj =
					result.data?.syncLatestGetexRegulations
						?.syncLatestGetexRegulationsResult;
				const success = resultObj?.success === true;
				if (success) {
					setMessage({
						message: t('SuccessTitle'),
						timeout: 10000,
						options: {
							messageBarType: MessageBarType.success,
						},
					});
				} else {
					setMessage({
						message: resultObj?.message
							? `${t('ErrorMessage')} ${resultObj.message}`
							: t('ErrorMessage'),
						timeout: 0,
						options: {
							messageBarType: MessageBarType.error,
						},
					});
				}
			} catch (e: unknown) {
				const detail = e instanceof Error ? e.message : undefined;
				setMessage({
					message: detail
						? `${t('ErrorMessage')} ${detail}`
						: t('ErrorMessage'),
					timeout: 0,
					options: {
						messageBarType: MessageBarType.error,
					},
				});
			}
		})();
	};

	const handleDismiss = () => {
		onDismiss();
	};

	return (
		<Dialog
			hidden={!isOpen}
			onDismiss={handleDismiss}
			dialogContentProps={{
				type: DialogType.normal,
				title: t('Title'),
			}}
		>
			<DialogFooter>
				<PrimaryButton onClick={handleConfirm} text={t('Confirm')} />
				<DefaultButton onClick={handleDismiss} text={t('Cancel')} />
			</DialogFooter>
		</Dialog>
	);
};
